<template>
  <div class="admin-landing mb-5">
    <div class="row g-4 mb-4">
      <h4 class="mb-2">Client Management</h4>
      <!-- Agencies Card -->
      <div class="col-12 col-md-6 col-lg-3 mt-2">
        <div class="card admin-card h-100" @click="navigateTo('/admin/agencies')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/building-08.svg" alt="Agencies" class="icon">
              </div>
              <h4 class="card-title mb-0">Agencies</h4>
            </div>
            <p class="card-text text-muted">Manage agency accounts and their configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Agencies →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Editor Card -->
      <div class="col-12 col-md-6 col-lg-3 mt-2">
        <div class="card admin-card h-100" @click="navigateTo('/admin/clients')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/user-circle.svg" alt="Client Editor" class="icon">
              </div>
              <h4 class="card-title mb-0">Clients</h4>
            </div>
            <p class="card-text text-muted">Manage client accounts and their configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Clients →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscriptions Card -->
      <div class="col-12 col-md-6 col-lg-3 mt-2">
        <div class="card admin-card h-100" @click="navigateTo('/admin/subscriptions')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/user-circle.svg" alt="Subscriptions" class="icon">
              </div>
              <h4 class="card-title mb-0">Billing</h4>
            </div>
            <p class="card-text text-muted">Manage client payment schedules, upcoming invoices, and proposals</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Billing →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Connection Status Card -->
      <div class="col-12 col-md-6 col-lg-3 mt-2">
        <div class="card admin-card h-100" @click="navigateTo('/admin/connection_status')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="d-flex align-items-center">
                <div class="admin-card-icon me-3">
                  <img src="/img/icons/avid-icons/plug-connected.svg" alt="Connection Status" class="icon">
                </div>
                <h4 class="card-title mb-0">Connections</h4>
              </div>
              <span v-if="connectionErrorCount > 0" class="rounded-pill badge bg-danger text-white border-0">
                    {{ connectionErrorCount }}
                </span>
            </div>
            <p class="card-text text-muted">Monitor and manage system connections and their status</p>
            <div class="mt-auto">
              <span class="text-primary">View Connection Status →</span>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="row g-2 mb-4">
      <hr class="divider w-100">
      <h4 class="mb-2">Reporting Management</h4>

      <!-- Report Group Builder Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/report_group_builder')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/cube-01.svg" alt="Report Group Builder" class="icon">
              </div>
              <h4 class="card-title mb-0">Report Groups</h4>
            </div>
            <p class="card-text text-muted">Organize reports into groups with custom styling and ordering</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Report Groups →</span>
            </div>
          </div>
        </div>
      </div>


      <!-- Report Builder Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/report_builder')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/cube-01.svg" alt="Report Builder" class="icon">
              </div>
              <h4 class="card-title mb-0">Report Editor</h4>
            </div>
            <p class="card-text text-muted">Create and manage report templates and configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Build Reports →</span>
            </div>
          </div>
        </div>
      </div>
      

      <!-- Chart Builder Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/charts')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/bar-chart-square-01.svg" alt="Chart Builder" class="icon">
              </div>
              <h4 class="card-title mb-0">Charts</h4>
            </div>
            <p class="card-text text-muted">Create and manage chart configurations and templates</p>
            <div class="mt-auto">
              <span class="text-primary">Build Charts →</span>
            </div>
          </div>
        </div>
      </div>


      <!-- Calculations / Filters Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/metrics_filters')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/calculator.svg" alt="Calculations / Filters" class="icon">
              </div>
              <h4 class="card-title mb-0">Calculations / Filters</h4>
            </div>
            <p class="card-text text-muted">Manage metric calculations and data filters</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Calculations / Filters →</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-2 mb-4">

      <hr class="divider w-100">
      <h4 class="mb-2">Miscellaneous</h4>
      <!-- Static Pages Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/pages')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/note-2.svg" alt="Static Pages" class="icon">
              </div>
              <h4 class="card-title mb-0">Static Pages</h4>
            </div>
            <p class="card-text text-muted">Create and manage static content pages</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Static Pages →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Playbook Editor Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/playbooks/list')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/target-04.svg" alt="Playbook Editor" class="icon">
              </div>
              <h4 class="card-title mb-0">Playbooks</h4>
            </div>
            <p class="card-text text-muted">Create and manage playbook templates and configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Edit Playbooks →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Segments Templates Card -->
      <div class="col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/transformation_segments')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/filter-funnel-01.svg" alt="Segments Templates" class="icon">
              </div>
              <h4 class="card-title mb-0">Audience Templates</h4>
            </div>
            <p class="card-text text-muted">Manage transformation audience templates and configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Audience Templates →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pathway Mapping Template Editor Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/pathway_mapping_templates')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/route.svg" alt="Pathway Mapping Template Editor" class="icon">
              </div>
              <h4 class="card-title mb-0">Pathway Mapping Templates</h4>
            </div>
            <p class="card-text text-muted">Manage pathway mapping templates for audience sync</p>
            <div class="mt-auto">
              <span class="text-primary">Manage Pathway Mapping Templates →</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-2 mb-4">
      <!-- Initiative Editor Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/initiative_editor')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/tool-01.svg" alt="Initiative Editor" class="icon">
              </div>
              <h4 class="card-title mb-0">Initiatives <em>(NextAfter Only)</em></h4>
            </div>
            <p class="card-text text-muted">Create and manage initiative templates and configurations</p>
            <div class="mt-auto">
              <span class="text-primary">Edit Initiatives →</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Shorthand URLs Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/shorthand_urls')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/route.svg" alt="Shorthand URLs" class="icon">
              </div>
              <h4 class="card-title mb-0">Shorthand URLs</h4>
            </div>
            <p class="card-text text-muted">View all shorthand URLs that auto-resolve the client GUID for marketing and KB articles</p>
            <div class="mt-auto">
              <span class="text-primary">View Shorthand URLs →</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-2 mb-4">
      <hr class="divider w-100">
      <h4 class="mb-2">Debug / Dev Tools</h4>
      <!-- Sixtran Debug Console Card -->
      <div class="col-12 col-md-6 col-lg-3">
        <div class="card admin-card h-100" @click="navigateTo('/admin/sixtran/search')">
          <div class="card-body d-flex flex-column">
            <div class="d-flex align-items-center mb-3">
              <div class="admin-card-icon me-3">
                <img src="/img/icons/avid-icons/cube-01.svg" alt="Sixtran Debug" class="icon">
              </div>
              <h4 class="card-title mb-0">Sixtran Debug Console</h4>
            </div>
            <p class="card-text text-muted">Search and inspect Client → Integration → Segment sync runs</p>
            <div class="mt-auto">
              <span class="text-primary">Open Console →</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminLanding',
  
  data() {
    return {
      connectionErrorCount: 0
    }
  },

  mounted() {
    this.fetchConnectionErrorCount();
  },

  methods: {
    navigateTo(url) {
      window.location.href = url;
    },

    async fetchConnectionErrorCount() {
      try {
        const response = await window.axios.get('/api/admin/connection-error-count');
        this.connectionErrorCount = response.data.count || 0;
      } catch (error) {
        console.error('Failed to fetch connection error count:', error);
        this.connectionErrorCount = 0;
      }
    }
  }
}
</script>

